import React, { useState } from 'react';
import Wrapper from './styles';
import { formatPrice, getThumbnailUrl } from '../../utils/helpers';
import { Link, useHistory } from 'react-router-dom';
import { useCartContext } from '../../context/cart_context';
import { useLanguageContext } from '../../context/language_context';
import { useWishlistContext } from '../../context/wishlist_context';
import { Star, ShoppingBag, Heart, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

const Product = ({ image, name, price, id, category, stock, colors, sizes, variants, shipping, originalPrice: productOriginalPrice, discountPercentage: productDiscountPercent, isOffer, rating = 0, numberOfReviews = 0, offers = [] }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { addToCart } = useCartContext();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlistContext();
  const { isArabic } = useLanguageContext();
  const history = useHistory();

  const isWishlisted = isInWishlist(id);

  // Determine if product is new (for demo, we'll show badge randomly based on id)
  const isNew = id && (parseInt(id.slice(-1), 16) % 3 === 0);
  
  // Use product's original price and discount if available, otherwise calculate demo values
  const hasDiscount = isOffer || (id && (parseInt(id.slice(-2), 16) % 4 === 0));
  const originalPrice = productOriginalPrice || (hasDiscount ? Math.round(price * 1.25) : null);
  const discountPercent = productDiscountPercent || (originalPrice ? Math.round((1 - price / originalPrice) * 100) : 0);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Build default variant selections from product variants
    const defaultVariantSelections = {};
    if (variants && variants.length > 0) {
      variants.forEach((v) => {
        if (v.values && v.values.length > 0) {
          defaultVariantSelections[v.name] = v.values[0];
        }
      });
    }
    const defaultColor = colors?.[0] || '';
    const defaultSize = sizes?.[0] || sizes || '';
    addToCart(id, defaultColor, defaultSize, 1, {
      id,
      name,
      price,
      image,
      colors: colors || [],
      sizes: sizes || [],
      variants: variants || [],
      shipping,
      stock: stock || 10
    }, Object.keys(defaultVariantSelections).length > 0 ? defaultVariantSelections : undefined);
    toast.success(isArabic ? '✓ تمت الإضافة للسلة' : '✓ Added to cart', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: true,
    });
  };

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWishlisted) {
      removeFromWishlist(id);
      toast.info(isArabic ? 'تمت الإزالة من المفضلة' : 'Removed from wishlist', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
      });
    } else {
      addToWishlist({
        _id: id,
        id,
        name,
        price,
        image,
        colors: colors || [],
        sizes: sizes || [],
        variants: variants || [],
        shipping,
        stock: stock || 10,
        category
      });
      toast.success(isArabic ? '♥ تمت الإضافة للمفضلة' : '♥ Added to wishlist', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(`/products/${id}`);
  };

  const handleProductClick = (e) => {
    // Only navigate if the click wasn't on a button
    if (e.currentTarget === e.target || !e.target.closest('button')) {
      history.push(`/products/${id}`);
    }
  };

  return (
    <Wrapper 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='product-card'>
        <div className='container' onClick={handleProductClick} style={{ cursor: 'pointer' }}>
          <img
            src={getThumbnailUrl(image, 500)}
            alt={name}
            loading='lazy'
            referrerPolicy='no-referrer'
            style={{ transform: isHovered ? 'scale(1.08)' : 'scale(1)' }}
          />
          
          {/* Badges */}
          <div className='badges'>
            {discountPercent > 0 ? (
              <span className='badge badge-sale'>
                -{discountPercent}%
              </span>
            ) : isNew ? (
              <span className='badge badge-new'>
                {isArabic ? 'جديد' : 'New'}
              </span>
            ) : null}
          </div>

          {/* Action Buttons - Heart and Eye */}
          <div className={`action-buttons ${isHovered ? 'visible' : ''}`}>
            <button 
              className={`action-btn wishlist-btn ${isWishlisted ? 'active' : ''}`}
              onClick={handleToggleWishlist}
              title={isWishlisted ? (isArabic ? 'إزالة من المفضلة' : 'Remove from wishlist') : (isArabic ? 'إضافة للمفضلة' : 'Add to wishlist')}
            >
              <Heart size={16} fill={isWishlisted ? '#ef4444' : 'none'} stroke={isWishlisted ? '#ef4444' : 'currentColor'} />
            </button>
            <button 
              className='action-btn quickview-btn'
              onClick={handleQuickView}
              title={isArabic ? 'عرض سريع' : 'Quick view'}
            >
              <Eye size={16} />
            </button>
          </div>

          {/* Quick Add Button - Desktop hover only */}
          <div className={`quick-add ${isHovered ? 'visible' : ''}`}>
            <button 
              className='quick-add-btn'
              onClick={handleAddToCart}
            >
              <ShoppingBag size={16} />
              <span>{isArabic ? 'إضافة للسلة' : 'Add to Cart'}</span>
            </button>
          </div>
        </div>

        <footer onClick={handleProductClick} style={{ cursor: 'pointer' }}>
          {/* Category label */}
          {category && (
            <span className='category-label'>{category}</span>
          )}
          
          <h5>{name}</h5>

          {/* Rating - Only show if there are reviews */}
          {numberOfReviews > 0 ? (
            <div className='rating'>
              <div className='stars'>
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i}
                    size={12}
                    className={i < Math.floor(rating) ? 'filled' : 'empty'}
                    fill={i < Math.floor(rating) ? '#C9A962' : 'none'}
                    stroke={i < Math.floor(rating) ? '#C9A962' : '#d1d5db'}
                  />
                ))}
              </div>
              <span className='review-count'>({numberOfReviews})</span>
            </div>
          ) : (
            <div style={{ height: '20px', marginBottom: '0.5rem' }}></div>
          )}

          {/* Price */}
          <div className='price-container'>
            {offers && offers.length > 0 ? (
              <>
                <span className='from-label'>{isArabic ? 'إبتداءً من' : 'À partir de'}</span>
                <span className='sale-price'>
                  {formatPrice(Math.min(...offers.map((o) => o.price)))}
                </span>
              </>
            ) : (
              <>
                <span className={originalPrice ? 'sale-price' : ''}>{formatPrice(price)}</span>
                {originalPrice && (
                  <span className='original-price'>{formatPrice(originalPrice)}</span>
                )}
              </>
            )}
          </div>

          {/* Mobile Add to Cart Button */}
          <button 
            className='mobile-add-btn'
            onClick={handleAddToCart}
          >
            <ShoppingBag size={14} />
            <span>{isArabic ? 'إضافة' : 'Add'}</span>
          </button>
        </footer>
      </div>
    </Wrapper>
  );
};

export default Product;
